import { Button, CircularProgress, TextField } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiEdit2, FiRotateCcw, FiSave, FiTrash2 } from "react-icons/fi";
import {
  TechnicalReportSignatureInterface,
  TechnicalReportSignatureRoleValue
} from "../../common/interfaces";
import SignaturePad, {
  SignaturePadHandle
} from "../../components/signature/SignaturePad";
import { deleteSignature, listSignatures, saveSignature } from "./signatureActions";
import {
  LockedHint,
  PadActions,
  PadError,
  PanelTitle,
  PanelWrapper,
  RoleBadge,
  RoleCard,
  RoleGrid,
  RoleHeader,
  SignedPreview
} from "./documents.styled";

const ROLES: {
  role: TechnicalReportSignatureRoleValue;
  label: string;
  namePlaceholder: string;
}[] = [
  {
    role: "TECHNICIAN",
    label: "Técnico autorizado",
    namePlaceholder: "Nome do técnico"
  },
  { role: "CLIENT", label: "Cliente", namePlaceholder: "Nome de quem recebeu" }
];

interface SignaturesPanelProps {
  reportId: number;
  /** Relatório concluído: o backend recusa assinar até ser reaberto. */
  isLocked?: boolean;
  canDelete?: boolean;
  onNotify?: (message: string) => void;
  onError?: (error: unknown) => void;
  /** Avisa a tela hospedeira para atualizar contadores/listas. */
  onChanged?: () => void;
}

/**
 * Colhe as duas assinaturas de um relatório técnico. Vive tanto na tela de
 * Documentos > Assinaturas quanto dentro do próprio relatório, porque o
 * técnico normalmente assina no mesmo lugar em que preenche o laudo.
 */
export default function SignaturesPanel({
  reportId,
  isLocked = false,
  canDelete = false,
  onNotify,
  onError,
  onChanged
}: SignaturesPanelProps) {
  const [signatures, setSignatures] = useState<
    TechnicalReportSignatureInterface[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] =
    useState<TechnicalReportSignatureRoleValue | null>(null);
  const [busyRole, setBusyRole] =
    useState<TechnicalReportSignatureRoleValue | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [documents, setDocuments] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<
    Record<string, { name?: string; drawing?: string }>
  >({});

  const padRefs = {
    TECHNICIAN: useRef<SignaturePadHandle>(null),
    CLIENT: useRef<SignaturePadHandle>(null)
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSignatures(await listSignatures(reportId));
    } catch (error) {
      console.error("Erro ao buscar assinaturas:", error);
      onError?.(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  useEffect(() => {
    load();
  }, [load]);

  const findSignature = (role: TechnicalReportSignatureRoleValue) =>
    signatures.find((s) => s.role === role);

  const startEditing = (role: TechnicalReportSignatureRoleValue) => {
    const existing = findSignature(role);
    setNames((prev) => ({ ...prev, [role]: existing?.signer_name ?? "" }));
    setDocuments((prev) => ({
      ...prev,
      [role]: existing?.signer_document ?? ""
    }));
    setErrors((prev) => ({ ...prev, [role]: {} }));
    setEditingRole(role);
  };

  const handleSave = async (role: TechnicalReportSignatureRoleValue) => {
    const name = (names[role] ?? "").trim();
    const image = padRefs[role].current?.toDataURL();

    // Junta tudo que impede o salvamento e mostra de uma vez — no toast e
    // marcando o campo — em vez de bloquear o botão sem explicar o motivo.
    const problems: { field: "name" | "drawing"; message: string }[] = [];
    if (name.length < 2) {
      problems.push({
        field: "name",
        message: "Informe o nome de quem está assinando."
      });
    }
    if (!image) {
      problems.push({
        field: "drawing",
        message: "Desenhe a assinatura no quadro antes de salvar."
      });
    }

    if (problems.length > 0) {
      setErrors((prev) => ({
        ...prev,
        [role]: {
          name: problems.find((p) => p.field === "name")?.message,
          drawing: problems.find((p) => p.field === "drawing")?.message
        }
      }));
      problems.forEach((p) => onError?.(new Error(p.message)));
      return;
    }

    setErrors((prev) => ({ ...prev, [role]: {} }));
    setBusyRole(role);
    try {
      await saveSignature(reportId, role, {
        signer_name: name,
        signer_document: (documents[role] ?? "").trim() || undefined,
        // Não-nulo aqui: a ausência do traço vira "problem" logo acima.
        image_base64: image as string
      });
      setEditingRole(null);
      await load();
      onNotify?.("Assinatura registrada!");
      onChanged?.();
    } catch (error) {
      console.error("Erro ao salvar assinatura:", error);
      onError?.(error);
    } finally {
      setBusyRole(null);
    }
  };

  const handleDelete = async (role: TechnicalReportSignatureRoleValue) => {
    setBusyRole(role);
    try {
      await deleteSignature(reportId, role);
      await load();
      onNotify?.("Assinatura removida.");
      onChanged?.();
    } catch (error) {
      console.error("Erro ao remover assinatura:", error);
      onError?.(error);
    } finally {
      setBusyRole(null);
    }
  };

  if (loading) {
    return (
      <PanelWrapper>
        <PanelTitle>Assinaturas</PanelTitle>
        <CircularProgress size={24} />
      </PanelWrapper>
    );
  }

  return (
    <PanelWrapper>
      <PanelTitle>Assinaturas</PanelTitle>
      {isLocked && (
        <LockedHint>
          Relatório concluído — reabra o relatório para assinar ou refazer uma
          assinatura.
        </LockedHint>
      )}

      <RoleGrid>
        {ROLES.map(({ role, label, namePlaceholder }) => {
          const signature = findSignature(role);
          const editing = editingRole === role;
          const busy = busyRole === role;

          return (
            <RoleCard key={role}>
              <RoleHeader>
                <span className="role-name">{label}</span>
                <RoleBadge $signed={!!signature}>
                  {signature ? "Assinado" : "Pendente"}
                </RoleBadge>
              </RoleHeader>

              {signature && !editing && (
                <SignedPreview>
                  <div className="ink">
                    {signature.url ? (
                      <img src={signature.url} alt={`Assinatura ${label}`} />
                    ) : (
                      <span className="meta">Imagem indisponível</span>
                    )}
                  </div>
                  <span className="who">{signature.signer_name}</span>
                  {signature.signer_document && (
                    <span className="meta">{signature.signer_document}</span>
                  )}
                  <span className="meta">
                    Assinado em{" "}
                    {new Date(signature.signed_at).toLocaleString("pt-BR")}
                  </span>
                  <PadActions>
                    <Button
                      size="small"
                      startIcon={<FiEdit2 />}
                      disabled={isLocked || busy}
                      onClick={() => startEditing(role)}
                    >
                      Refazer
                    </Button>
                    {canDelete && (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<FiTrash2 />}
                        disabled={isLocked || busy}
                        onClick={() => handleDelete(role)}
                      >
                        Remover
                      </Button>
                    )}
                  </PadActions>
                </SignedPreview>
              )}

              {!signature && !editing && (
                <>
                  <LockedHint>
                    Nenhuma assinatura registrada para este papel.
                  </LockedHint>
                  <PadActions>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={isLocked}
                      onClick={() => startEditing(role)}
                    >
                      Assinar
                    </Button>
                  </PadActions>
                </>
              )}

              {editing && (
                <>
                  <TextField
                    size="small"
                    required
                    label="Nome de quem assina"
                    placeholder={namePlaceholder}
                    value={names[role] ?? ""}
                    disabled={busy}
                    error={!!errors[role]?.name}
                    helperText={errors[role]?.name}
                    onChange={(e) => {
                      setNames((prev) => ({ ...prev, [role]: e.target.value }));
                      if (errors[role]?.name) {
                        setErrors((prev) => ({
                          ...prev,
                          [role]: { ...prev[role], name: undefined }
                        }));
                      }
                    }}
                  />
                  <TextField
                    size="small"
                    label="Documento (opcional)"
                    placeholder="CPF ou RG"
                    value={documents[role] ?? ""}
                    disabled={busy}
                    onChange={(e) =>
                      setDocuments((prev) => ({
                        ...prev,
                        [role]: e.target.value
                      }))
                    }
                  />
                  <SignaturePad
                    ref={padRefs[role]}
                    disabled={busy}
                    onDrawnChange={(hasDrawing) => {
                      if (hasDrawing && errors[role]?.drawing) {
                        setErrors((prev) => ({
                          ...prev,
                          [role]: { ...prev[role], drawing: undefined }
                        }));
                      }
                    }}
                  />
                  {errors[role]?.drawing && (
                    <PadError>{errors[role]?.drawing}</PadError>
                  )}
                  <PadActions>
                    <Button
                      size="small"
                      startIcon={<FiRotateCcw />}
                      disabled={busy}
                      onClick={() => padRefs[role].current?.clear()}
                    >
                      Limpar
                    </Button>
                    <Button size="small" disabled={busy} onClick={() => setEditingRole(null)}>
                      Cancelar
                    </Button>
                    {/* Só o envio em andamento desabilita: o que falta
                        preencher é dito no clique, não escondido num botão
                        cinza. */}
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={busy ? undefined : <FiSave />}
                      disabled={busy}
                      onClick={() => handleSave(role)}
                    >
                      {busy ? <CircularProgress size={18} /> : "Salvar"}
                    </Button>
                  </PadActions>
                </>
              )}
            </RoleCard>
          );
        })}
      </RoleGrid>
    </PanelWrapper>
  );
}
